import { Shift, useConfig } from '@nexelem/config';
import { Chip, DropdownMenu } from '@nexelem/components';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { useCustomTranslation } from '../../hooks';
import { add, format } from 'date-fns';

type ShiftSelectorProps = {
  selectedShifts: Shift['nameKey'][];
  setSelectedShifts: (selectedShifts: Shift['nameKey'][]) => void;
};

export const ShiftSelector = ({ selectedShifts, setSelectedShifts }: ShiftSelectorProps) => {
  const { t } = useCustomTranslation('products.productionPlan');
  const { modules } = useConfig();

  const shifts = modules.production.workerModeViewShifts;

  const handleSelectOption = (option: Shift['nameKey']) => (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedShifts.includes(option)) {
      setSelectedShifts(selectedShifts.filter((o) => o !== option));
    } else {
      setSelectedShifts([...selectedShifts, option]);
    }
  };

  const firstShift =
    selectedShifts[0] !== undefined ? shifts.find((shift) => shift.nameKey === selectedShifts[0]) : undefined;
  const label = `${t('common.shift')}${firstShift ? `: ${getShiftName(firstShift)}` : ''}`;
  const count = selectedShifts.length > 1 ? `+${selectedShifts.length - 1}` : undefined;

  const currentShift = getCurrentShift(shifts);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Chip
          className="shrink-0"
          count={count}
          EndIcon={<CUSTOM_ICONS.CaretDown />}
          label={label}
          selected={selectedShifts.length > 0}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        {shifts.map((shift) => (
          <DropdownMenu.Checkbox
            checked={selectedShifts.includes(shift.nameKey)}
            disabled={isShiftDisabled(shift.nameKey, selectedShifts)}
            key={shift.nameKey}
            onSelect={handleSelectOption(shift.nameKey)}
          >
            {getShiftName(shift)}{' '}
            {currentShift?.nameKey === shift.nameKey ? `(${t('common.current').toLowerCase()})` : ''}
          </DropdownMenu.Checkbox>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export const isShiftDisabled = (shift: Shift['nameKey'], selectedShifts: Shift['nameKey'][]) => {
  if (selectedShifts.length === 0) {
    return false;
  }

  if (shift === 'shift1') {
    if (selectedShifts.includes('shift1')) {
      return false;
    } else {
      return !selectedShifts.includes('shift2') && selectedShifts.includes('shift3');
    }
  }

  if (shift === 'shift2') {
    if (selectedShifts.includes('shift2')) {
      return selectedShifts.includes('shift3') && selectedShifts.includes('shift1');
    } else {
      return false;
    }
  }

  if (shift === 'shift3') {
    if (selectedShifts.includes('shift3')) {
      return false;
    } else {
      return selectedShifts.includes('shift1') && !selectedShifts.includes('shift2');
    }
  }

  return false;
};

export const getShiftName = (shift: Shift) => `${shift.shortName} ${shift.from} - ${shift.to}`;

export const getCurrentShift = (shifts: Shift[]) => {
  const now = new Date();
  // Convert current time to minutes from midnight
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const currentShift = shifts.find((shift) => {
    const [startH, startM] = splitHoursAndMinutes(shift.from);
    const [endH, endM] = splitHoursAndMinutes(shift.to);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    // Case A: Shift stays within the same day (e.g., 06:30 to 14:30)
    // Case B: Shift crosses midnight (e.g., 22:30 to 06:30)
    if (startMinutes < endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    } else {
      return currentMinutes >= startMinutes || currentMinutes < endMinutes;
    }
  });

  if (currentShift) {
    return {
      nameKey: currentShift.nameKey,
      from: new Date(`${format(now, 'yyyy-MM-dd')} ${currentShift.from}`),
      to: add(new Date(`${format(now, 'yyyy-MM-dd')} ${currentShift.to}`), {
        days: currentShift.endNextDay ? 1 : 0,
        seconds: -1,
      }),
    };
  }
};

const splitHoursAndMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);

  if (hours === undefined || minutes === undefined) {
    throw new Error(`Invalid time: ${time}`);
  }

  return [hours, minutes] as const;
};
